import type { AnyRouter } from '@tanstack/react-router'
import { type JSX, type ReactNode, createContext, use, useMemo } from 'react'

import { usePreferences } from '../hooks/use-preferences.ts'
import { updatePreferences } from '../lib/preference-store.ts'
import {
  browserTimeZone,
  parseTimeZone,
  UTC_TIME_ZONE
} from '../lib/time-zone.ts'
import { useDisplaying } from './display-runtime.tsx'

/** What `useTimeZone` hands a consumer. */
export interface TimeZoneContextValue {
  /**
   * The zone in force. An unset choice is UTC, unless a display is on and
   * the reader saved no zone. Then it is the browser's zone.
   */
  timeZone: string
  /** Hold a zone, and apply it to this tab. */
  setTimeZone: (timeZone: string) => void
}

/**
 * Hold a zone in the reader's settings. A name this console does not apply is
 * ignored, so a bad value can reach neither the cache nor the server.
 *
 * @param next - The zone the reader chose.
 */
function holdTimeZone(next: string): void {
  const zone = parseTimeZone(next)
  if (zone !== null) {
    void updatePreferences({ timeZone: zone })
  }
}

const TimeZoneContext = createContext<TimeZoneContextValue>({
  timeZone: UTC_TIME_ZONE,
  setTimeZone: holdTimeZone
})

/**
 * Read the reader's zone, and change it.
 *
 * Outside a provider this is UTC, and a write still persists.
 * A screen test that renders no provider keeps working. The rail sets the
 * zone through the provider, so a change re-renders every report.
 *
 * @returns The zone context.
 */
export function useTimeZone(): TimeZoneContextValue {
  return use(TimeZoneContext)
}

/**
 * The zone context for one screen.
 *
 * A saved zone wins. With no saved zone, a display uses the browser zone,
 * because the kiosk clock is the zone the page should resolve. The console
 * stays on UTC until the reader chooses.
 *
 * @param props - Source props.
 * @param props.children - The app.
 * @param props.displaying - Whether a display owns the screen.
 * @returns The provider.
 */
function TimeZoneSource({
  children,
  displaying
}: {
  children: ReactNode
  displaying: boolean
}): JSX.Element {
  const stored = usePreferences().timeZone
  const timeZone = stored ?? (displaying ? browserTimeZone() : UTC_TIME_ZONE)
  const value = useMemo(
    () => ({ timeZone, setTimeZone: holdTimeZone }),
    [timeZone]
  )

  return <TimeZoneContext value={value}>{children}</TimeZoneContext>
}

/**
 * Read whether a display owns the screen, then provide the zone.
 *
 * Split from `TimeZoneProvider` so the router hook is not conditional.
 *
 * @param props - Source props.
 * @param props.children - The app.
 * @param props.router - The app router.
 * @returns The provider.
 */
function DisplayTimeZone({
  children,
  router
}: {
  children: ReactNode
  router: AnyRouter
}): JSX.Element {
  const displaying = useDisplaying(router)

  return <TimeZoneSource displaying={displaying}>{children}</TimeZoneSource>
}

/**
 * TimeZoneProvider — the reader's zone for every report.
 *
 * Sits beside the theme provider, above both the signed-in app and the
 * sign-in screen, so signing in does not remount it and drop the choice.
 * Pass the router so a display with no saved zone can use the browser zone.
 * Without a router the fallback stays UTC, which is what a screen test uses.
 *
 * @param props - Provider props.
 * @param props.children - The app.
 * @param props.router - The app router, when the tree has one.
 * @returns The provider.
 */
export function TimeZoneProvider({
  children,
  router
}: {
  children: ReactNode
  router?: AnyRouter
}): JSX.Element {
  if (router === undefined) {
    return <TimeZoneSource displaying={false}>{children}</TimeZoneSource>
  }

  return <DisplayTimeZone router={router}>{children}</DisplayTimeZone>
}
